import React, { useState, useEffect, useMemo } from 'react';
import '../../App.css';
import '../common/RequestsTable.css';
import { LuRefreshCw } from "react-icons/lu";
import { SEC_ApiCall } from '../func.js';
import { ApiError } from '../common/apiError.js';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { FiExternalLink, FiInfo } from 'react-icons/fi';
import { CgSpinner } from 'react-icons/cg';
import LoadingComp from '../common/loading.js';
import { AddNote } from '../common/toolbar/AddNote.js';
import { MarkCompleteButton } from '../common/toolbar/MarkCompleteButton.js';
import { MarkIncompleteButton } from '../common/toolbar/MarkIncompleteButton.js';
import { NoteInputComponent } from '../common/toolbar/NoteInputComponent.js';
import Select from 'react-select';
import Header from '../common/header.js';


export default function ItUserRequests() {
  const navigate = useNavigate();

  const [dataState, setDataState] = useState([]);
  const [isLoadedState, setIsLoadedState] = useState(false);
  const [apiErrorState, setApiErrorState] = useState(null);
  const [apiErrorTextState, setApiErrorTextState] = useState('');

  const [resultsPerPageState, setResultsPerPageState] = useState(12);
  const [currentPageState, setCurrentPageState] = useState(1);
  const [numberOfPagesState, setNumberOfPagesState] = useState(1);
  const [pageDataState, setPageDataState] = useState(null);
  const [isRefreshingState, setIsRefreshingState] = useState(false);
  const [requestStateFilter, setRequestStateFilter] = useState('incomplete');
  const [statusFilterState, setStatusFilterState] = useState('all');
  const [assignedFilterState, setAssignedFilterState] = useState('all');
  const [sortFieldState, setSortFieldState] = useState('date');
  const [sortOrderState, setSortOrderState] = useState({ value: 'earliestToLatest', label: 'Nearest first' });

  function paginate(array, page_size, page_number) {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  }

  async function apiCall(urlQuery) {
    setIsRefreshingState(true);
    var url = 'it-requests/user';
    var params = {
      'query': urlQuery
    };
    try {
      const responseString = await SEC_ApiCall(url, params, setApiErrorState);
      const response = JSON.parse(responseString);
      setDataState(response);
      setCurrentPageState(1);
      setIsLoadedState(true);
    } catch (e) {
      setIsLoadedState(true);
      setApiErrorState('error');
      setApiErrorTextState(e);
    } finally {
      setIsRefreshingState(false);
    }
  };

  async function fetchRequestDetails(requestId) {
    const responseString = await SEC_ApiCall('it-requests/user/single', { requestId });
    return JSON.parse(responseString);
  }

  useEffect(() => {
    apiCall('incomplete');
  }, []);

  function dataReload() {
    apiCall(requestStateFilter);
  };

  const statusOptionsForFilter = useMemo(() => {
    const namesById = new Map();
    dataState.forEach((request) => {
      const assignedUser = request?.assignedUser && request.assignedUser !== 'None' ? request.assignedUser : 'Unassigned';
      if (assignedFilterState !== 'all' && assignedUser !== assignedFilterState) {
        return;
      }
      const statusId = request?.status && typeof request.status === 'object' ? request.status.id : request?.status;
      const statusName = request?.status && typeof request.status === 'object'
        ? request.status.name
        : statusId || 'Open';
      if (statusId || statusName) {
        namesById.set(statusId || statusName, statusName);
      }
    });
    return Array.from(namesById.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [dataState, assignedFilterState]);

  const assignedUserOptions = useMemo(() => {
    const names = new Set();
    dataState.forEach((request) => {
      const statusId = request?.status && typeof request.status === 'object' ? request.status.id : request?.status;
      const statusName = request?.status && typeof request.status === 'object'
        ? request.status.name
        : statusId || 'Open';
      if (statusFilterState !== 'all') {
        if (statusId) {
          if (statusId !== statusFilterState) {
            return;
          }
        } else if (statusName !== statusFilterState) {
          return;
        }
      }
      const assignedName = request?.assignedUser && request.assignedUser !== 'None' ? request.assignedUser : 'Unassigned';
      names.add(assignedName);
    });
    return Array.from(names).sort();
  }, [dataState, statusFilterState]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(dataState)) {
      return [];
    }
    return dataState.filter((request) => {
      if (statusFilterState !== 'all') {
        const statusId = request?.status && typeof request.status === 'object' ? request.status.id : request?.status;
        const statusName = request?.status && typeof request.status === 'object'
          ? request.status.name
          : statusId || 'Open';
        if (statusId) {
          if (statusId !== statusFilterState) {
            return false;
          }
        } else if (statusName !== statusFilterState) {
          return false;
        }
      }

      if (assignedFilterState !== 'all') {
        const assignedUser = request?.assignedUser && request.assignedUser !== 'None' ? request.assignedUser : 'Unassigned';
        if (assignedUser !== assignedFilterState) {
          return false;
        }
      }

      return true;
    });
  }, [dataState, statusFilterState, assignedFilterState]);

  const sortOrderOptions = useMemo(() => {
    if (sortFieldState === 'id') {
      return [
        { value: 'lowToHigh', label: 'Low to high' },
        { value: 'highToLow', label: 'High to low' }
      ];
    }
    return [
      { value: 'earliestToLatest', label: 'Nearest first' },
      { value: 'latestToEarliest', label: 'Farthest first' }
    ];
  }, [sortFieldState]);

  const sortedFilteredData = useMemo(() => {
    if (!Array.isArray(filteredData)) {
      return [];
    }
    const sorted = [...filteredData];
    sorted.sort((leftRequest, rightRequest) => {
      if (sortFieldState === 'id') {
        const leftId = parseInt(leftRequest?.requestId, 10) || 0;
        const rightId = parseInt(rightRequest?.requestId, 10) || 0;
        return sortOrderState?.value === 'highToLow' ? rightId - leftId : leftId - rightId;
      }
      const leftDate = parseRequestDateValue(leftRequest?.requiredDate);
      const rightDate = parseRequestDateValue(rightRequest?.requiredDate);
      if (sortOrderState?.value === 'earliestToLatest') {
        return leftDate - rightDate;
      }
      return rightDate - leftDate;
    });
    return sorted;
  }, [filteredData, sortFieldState, sortOrderState]);

  useEffect(() => {
    const resultsSize = sortedFilteredData.length;
    const perPage = resultsPerPageState === 'all' ? resultsSize : resultsPerPageState;
    const totalPages = perPage ? Math.ceil((resultsSize / perPage)) : 1;
    const safePage = Math.min(currentPageState, totalPages || 1);
    setNumberOfPagesState(totalPages || 1);
    if (resultsSize === 0) {
      setPageDataState([]);
      if (safePage !== 1) {
        setCurrentPageState(1);
      }
      return;
    }
    setPageDataState(paginate(sortedFilteredData, perPage, safePage));
    if (safePage !== currentPageState) {
      setCurrentPageState(safePage);
    }
  }, [sortedFilteredData, resultsPerPageState, currentPageState]);

  if (apiErrorState === 'error') {
    return (
      <ApiError />
    )
  } else {

    function nextPage() {
      let currentPage = currentPageState;
      if (currentPage >= numberOfPagesState) {
        setCurrentPageState(currentPage);
      } else {
        let nextPage = currentPage + 1;
        setCurrentPageState(nextPage);
      }
    }

    function prevPage() {
      let currentPage = currentPageState;
      if (currentPage > 1) {
        let nextPage = currentPage - 1;
        setCurrentPageState(nextPage);
      } else {
        setCurrentPageState(1);
      }
    }


    return (
      <div className='internal_main_div it-admin-requests-shell'>
        <Header
          title='My IT Requests'
          subtitle='Review and track the IT support requests you have submitted.'
          rightContent={(
            <button
              className='module-page-header__button module-page-header__button--icon'
              type='button'
              onClick={dataReload}
              disabled={!isLoadedState}
              aria-label='Refresh IT requests'
            >
              {isRefreshingState ? <CgSpinner className='reload-icon it-admin-spinner' /> : <LuRefreshCw className='reload-icon' />}
            </button>
          )}
        />
        <div className="it-admin-content-grid">
          <div className="it-admin-content-main">
            {!isLoadedState ? (
              <LoadingComp />
            ) : (
              <>
                <ItUserRequestsCards
                  data={pageDataState}
                  isLoaded={isLoadedState}
                  apiError={apiErrorState}
                  selectorValue={requestStateFilter}
                  onRefresh={() => apiCall(requestStateFilter)}
                  fetchRequestDetails={fetchRequestDetails}
                  onOpenFull={(requestId) => navigate(`/app/ItSupport/user/requests/srv?id=${requestId}`)}
                />
                <div className="nextRequestPageDiv">
                <div className="pageNumberButtons" onClick={prevPage}><FaArrowLeft color="#fb9233" /></div><div className="pageNumberDiv">{currentPageState} / {numberOfPagesState}</div><div className="pageNumberButtons" onClick={nextPage}><FaArrowRight color="#fb9233" /></div>
                </div>
              </>
            )}
          </div>
          <aside className="it-admin-filter-sidebar">
            <div className="it-admin-filter-panel">
              <label className="it-admin-filter-field">
                <span className="it-admin-filter-label">Request state</span>
                <select
                  className="it-admin-filter-select"
                  value={requestStateFilter}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    setRequestStateFilter(newValue);
                    setCurrentPageState(1);
                    apiCall(newValue);
                  }}
                >
                  <option value="incomplete">Incomplete</option>
                  <option value="complete">Complete</option>
                </select>
              </label>
              <label className="it-admin-filter-field">
                <span className="it-admin-filter-label">Request status</span>
                <select
                  className="it-admin-filter-select"
                  value={statusFilterState}
                  onChange={(event) => {
                    setStatusFilterState(event.target.value);
                    setCurrentPageState(1);
                  }}
                >
                  <option value="all">All statuses</option>
                  {statusOptionsForFilter.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="it-admin-filter-field">
                <span className="it-admin-filter-label">Assigned user</span>
                <select
                  className="it-admin-filter-select"
                  value={assignedFilterState}
                  onChange={(event) => {
                    setAssignedFilterState(event.target.value);
                    setCurrentPageState(1);
                  }}
                >
                  <option value="all">All assigned</option>
                  {assignedUserOptions.map((user) => (
                    <option key={user} value={user}>
                      {user}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="it-admin-filter-clear"
                onClick={() => {
                  setRequestStateFilter('incomplete');
                  setStatusFilterState('all');
                  setAssignedFilterState('all');
                  setCurrentPageState(1);
                  apiCall('incomplete');
                }}
                disabled={
                  requestStateFilter === 'incomplete'
                  && statusFilterState === 'all'
                  && assignedFilterState === 'all'
                }
              >
                Clear filters
              </button>
            </div>
            <div className="it-admin-filter-panel it-admin-sort-panel">
              <p className="it-admin-sort-title">Sort requests</p>
              <div className="it-admin-sort-radio-group">
                <label className="it-admin-sort-radio-option">
                  <input
                    type="radio"
                    name="itUserSortField"
                    value="date"
                    checked={sortFieldState === 'date'}
                    onChange={() => {
                      setSortFieldState('date');
                      setSortOrderState({ value: 'earliestToLatest', label: 'Nearest first' });
                      setCurrentPageState(1);
                    }}
                  />
                  Required Date
                </label>
                <label className="it-admin-sort-radio-option">
                  <input
                    type="radio"
                    name="itUserSortField"
                    value="id"
                    checked={sortFieldState === 'id'}
                    onChange={() => {
                      setSortFieldState('id');
                      setSortOrderState({ value: 'lowToHigh', label: 'Low to high' });
                      setCurrentPageState(1);
                    }}
                  />
                  ID number
                </label>
              </div>
              <label className="it-admin-filter-field">
                <span className="it-admin-filter-label">Sort order</span>
                <Select
                  className="it-admin-sort-order-select"
                  classNamePrefix="it-admin-sort"
                  options={sortOrderOptions}
                  value={sortOrderState}
                  onChange={(option) => {
                    if (!option) {
                      return;
                    }
                    setSortOrderState(option);
                    setCurrentPageState(1);
                  }}
                  isSearchable={false}
                />
              </label>
            </div>
          </aside>
        </div>

      </div>
    )
  }
};

function ItUserRequestsCards({ data, isLoaded, apiError, selectorValue, onRefresh, fetchRequestDetails, onOpenFull }) {
  if (!isLoaded) {
    return (
      <div className="it-admin-cards-empty">
        <LoadingComp />
      </div>
    );
  }

  if (apiError) {
    return <ApiError />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="it-admin-cards-empty">
        <p className="it-admin-cards-loading">No requests to show</p>
      </div>
    );
  }

  return (
    <div className="it-admin-cards-grid">
      {data.map((request) => (
        <ItUserRequestCard
          key={request.requestId}
          request={request}
          selectorValue={selectorValue}
          onRefresh={onRefresh}
              fetchRequestDetails={fetchRequestDetails}
          onOpenFull={() => onOpenFull(request.requestId)}
        />
      ))}
    </div>
  );
}

function ItUserRequestCard({ request, selectorValue, onRefresh, fetchRequestDetails, onOpenFull }) {
  const [activePanel, setActivePanel] = useState(null);
  const [detailsState, setDetailsState] = useState(null);
  const [detailsLoadingState, setDetailsLoadingState] = useState(false);
  const [detailsErrorState, setDetailsErrorState] = useState(false);

  const statusValue = useMemo(() => {
    if (request?.status && typeof request.status === 'object') {
      return request.status.name || 'Open';
    }
    if (request?.status) {
      return request.status;
    }
    if (selectorValue === 'complete') {
      return 'Complete';
    }
    return 'Open';
  }, [request, selectorValue]);

  const isComplete = selectorValue === 'complete' || request?.completed === 1 || statusValue === 'Complete';

  const statusColour = useMemo(() => {
    if (request?.status && typeof request.status === 'object' && request.status.colour) {
      return request.status.colour;
    }
    return '#f9b233';
  }, [request]);

  const statusTextColour = useMemo(() => {
    if (request?.status && typeof request.status === 'object' && request.status.textColour) {
      return request.status.textColour;
    }
    return '#ffffff';
  }, [request]);

  const loadDetails = async () => {
    if (detailsLoadingState || detailsState) {
      return;
    }
    setDetailsLoadingState(true);
    setDetailsErrorState(false);
    try {
      const response = await fetchRequestDetails(request.requestId);
      setDetailsState(response?.data || null);
    } catch (error) {
      console.error('Failed to load IT request details', error);
      setDetailsErrorState(true);
    } finally {
      setDetailsLoadingState(false);
    }
  };

  const handlePanelToggle = async (panel) => {
    if (activePanel === panel) {
      setActivePanel(null);
      return;
    }
    if (panel === 'details') {
      setActivePanel(panel);
      await loadDetails();
      return;
    }
    setActivePanel(panel);
  };

  const handleRefresh = () => {
    onRefresh();
    setActivePanel(null);
  };

  return (
    <div className={`it-admin-card${activePanel ? ' it-admin-card-active' : ''}`}>
      <div className="it-admin-card-main">
        <div className="it-admin-card-header">
          <div>
            <p className="it-admin-card-kicker">IT Request #{request.requestId}</p>
            <h3 className="it-admin-card-title">{request.requestTitle}</h3>
          </div>
          <span className="it-admin-card-status" style={{ background: statusColour, color: statusTextColour }}>
            {statusValue}
          </span>
        </div>

        <div className="it-admin-card-meta">
          <p><strong>Submitted by:</strong> {request.submittingUser || 'Unknown'}</p>
          {request.assignedUser && request.assignedUser !== 'None' && (
            <p><strong>Assigned to:</strong> {request.assignedUser}</p>
          )}
          {request.requiredDate && (
            <p><strong>Required date:</strong> {request.requiredDate}</p>
          )}
        </div>

        {activePanel === 'details' && (detailsState || detailsErrorState) && (
          <div className="it-admin-card-details">
            {detailsErrorState && <p className="it-admin-card-error">Unable to load details.</p>}
            {detailsState && (
              <>
                <p><strong>Location:</strong> {detailsState.requestLocation || 'Not set'}</p>
                <p><strong>Submitted on:</strong> {formatDateTime(detailsState.submissionTimestamp)}</p>
                <p className="it-admin-card-description">
                  <strong>Description:</strong> {detailsState.requestEvents?.primaryEvent?.eventData || 'No description'}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      <div className="it-admin-card-actions">
        <div className="it-admin-card-actions-icons">
          {isComplete ? (
            <MarkIncompleteButton
              onClick={() => handlePanelToggle('incomplete')}
              isActive={activePanel === 'incomplete'}
            />
          ) : (
            <MarkCompleteButton
              onClick={() => handlePanelToggle('complete')}
              isActive={activePanel === 'complete'}
            />
          )}
          <AddNote
            onClick={() => handlePanelToggle('note')}
            isActive={activePanel === 'note'}
          />
        </div>
        <div className="it-admin-card-actions-icons it-admin-card-actions-icons-right">
          <button
            type="button"
            className="it-admin-card-icon-button"
            onClick={() => handlePanelToggle('details')}
            aria-label={activePanel === 'details' ? 'Hide details' : 'View details'}
            title={activePanel === 'details' ? 'Hide details' : 'View details'}
          >
            {detailsLoadingState && activePanel === 'details' ? <CgSpinner className="it-admin-spinner" /> : <FiInfo />}
          </button>
          <button
            type="button"
            className="it-admin-card-icon-button it-admin-card-icon-button-secondary"
            onClick={onOpenFull}
            aria-label="Open full view"
            title="Open full view"
          >
            <FiExternalLink />
          </button>
        </div>
      </div>

      {activePanel === 'note' && (
        <div className="it-admin-card-panel">
          <NoteInputComponent
            requestId={request.requestId}
            url="it-requests/single-it-request/notes"
            updateApiCallFunction={() => handleRefresh()}
            placeholder="Enter your note here..."
            cardView
          />
        </div>
      )}

      {activePanel === 'complete' && (
        <div className="it-admin-card-panel">
          <NoteInputComponent
            requestId={request.requestId}
            url="it-requests/single-it-request/complete"
            updateApiCallFunction={() => handleRefresh()}
            placeholder="Enter your completion note..."
            cardView
          />
        </div>
      )}

      {activePanel === 'incomplete' && (
        <div className="it-admin-card-panel">
          <NoteInputComponent
            requestId={request.requestId}
            url="it-requests/single-it-request/incomplete"
            updateApiCallFunction={() => handleRefresh()}
            placeholder="Enter your reason for reopening..."
            cardView
            showPrivateToggle
          />
        </div>
      )}
    </div>
  );
}

function formatDateTime(rawValue) {
  if (!rawValue) {
    return 'Not set';
  }
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function parseRequestDateValue(rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return 0;
  }
  if (typeof rawValue === 'number') {
    return rawValue;
  }
  const directParse = new Date(rawValue).getTime();
  if (!Number.isNaN(directParse)) {
    return directParse;
  }
  if (typeof rawValue !== 'string') {
    return 0;
  }
  const normalisedValue = rawValue.replace(',', '').trim();
  const ukDateMatch = normalisedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (!ukDateMatch) {
    return 0;
  }
  const day = parseInt(ukDateMatch[1], 10);
  const month = parseInt(ukDateMatch[2], 10) - 1;
  const year = parseInt(ukDateMatch[3], 10);
  const hour = parseInt(ukDateMatch[4] || '0', 10);
  const minute = parseInt(ukDateMatch[5] || '0', 10);
  return new Date(year, month, day, hour, minute).getTime();
}
